
import type { PlayErrorField } from '@empathybroker/tsclient-play/dist/models/play-error-field'
import type { PlayErrorReason } from '@empathybroker/tsclient-play/dist/models/play-error-reason'

import type { PropType, SetupContext } from 'vue'
import type { ContextState, Filter, RegularFilter } from '../../models'

import type { EpbAccordionSection } from '../shared/epb-accordion.component.vue'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import { mapGetters } from '../../composables'
import { TYPES } from '../../config/inversify.types'
import { ColorEnum, FilterTypeEnum } from '../../enums'
import { CONTEXT_CONSTS } from '../../store'
import CollapsableLayout from '../collapsable-layout.component.vue'
import RegularFilterSelector from '../filters/regular-filter-selector.component.vue'
import { UiAlert } from '../keen-ui'
import EpbAccordion from '../shared/epb-accordion.component.vue'
import { EpbBadge } from '../shared/epb-badge'
import { EpbSwitch } from '../shared/epb-switch'
import { EpbText } from '../shared/epb-text'
import ToolingFormActionButtons from './tooling-form-action-buttons.component.vue'

export type ToolingFormLayoutSection = 'details' | 'filters' | 'settings' | 'preview'

export default defineComponent({
  name: 'ToolingFormLayout',
  components: {
    CollapsableLayout,
    EpbAccordion,
    EpbBadge,
    EpbSwitch,
    EpbText,
    RegularFilterSelector,
    ToolingFormActionButtons,
    UiAlert,
  },
  inject: {
    objectIdService: TYPES.ObjectIdService,
  },
  props: {
    enabled: {
      type: Boolean,
      default: undefined,
    },
    filters: {
      type: Array as PropType<Array<RegularFilter>>,
      default: () => [],
    },
    readonly: {
      type: Boolean,
    },
    disabledSections: {
      type: Array as PropType<Array<ToolingFormLayoutSection>>,
      default: () => [],
    },
    acceptActionDisabled: {
      type: Boolean,
    },
    updateMode: Boolean,
    errorsFields: {
      type: Array as PropType<Array<PlayErrorField>>,
      default: () => [],
    },
    messages: {
      type: Object,
      required: true,
    },
    custom: {
      type: Object as PropType<EpbAccordionSection>,
      required: false,
    },
    customIndex: {
      type: Number,
      default: undefined,
    },
    hidePreviewTitle: Boolean,
  },
  emits: ['setEnabled', 'setFilterSelectedValues', 'accept', 'cancel', 'exit'],
  setup(props, ctx: SetupContext) {
    const baseClass = 'epb-tooling-form-layout'

    const { emit } = ctx

    const {
      proxy: { $epb, $store },
    } = getCurrentInstance()

    const { context } = mapGetters<ContextState>($store, CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    })

    const showErrorContainer = ref(false)

    const configurationSections = computed<Array<EpbAccordionSection>>(() => {
      const configurations: EpbAccordionSection[] = [
        {
          id: 'details',
          title: props.messages.sections.details.title,
          disabled: props.disabledSections.includes('details'),
        },
      ]

      if (props.filters.length) {
        configurations.push({
          id: 'filters',
          title: props.messages.sections.filters?.title,
          disabled: props.disabledSections.includes('filters'),
        })
      }

      configurations.push({
        id: 'settings',
        title: props.messages.sections.settings.title,
        disabled: props.disabledSections.includes('settings'),
      })

      if (props.custom) {
        if (props.customIndex) {
          configurations.splice(props.customIndex, 0, props.custom)
        } else {
          configurations.push(props.custom)
        }
      }

      return configurations
    })

    const getErrorFieldHeaderText = function (errorField: PlayErrorField): string {
      let headerText: string
      let fieldId
      if (!Array.isArray(errorField.fieldId)) {
        fieldId = errorField.fieldId.split('.').pop()
      } else {
        fieldId = errorField.fieldId
          .map(field => {
            const fieldMessage = props.messages.fields[field.split('.').pop()]
            return (!!fieldMessage && fieldMessage.label) || field
          })
          .join(', ')
      }
      const fieldMessages = props.messages.fields[fieldId]
      if (fieldMessages) {
        headerText = fieldMessages.label
      } else {
        const fieldFilter = Object.values(context.value.filters).find(
          (filter: Filter) =>
            filter.config.type === FilterTypeEnum.REGULAR &&
            ((filter as RegularFilter).config.toolingId || filter.config.id) === fieldId,
        ) as RegularFilter
        if (fieldFilter) {
          const filtersNames = $epb.messages.filters.names
          headerText =
            fieldFilter.config.prettyName ||
            filtersNames[fieldFilter.config.id] ||
            fieldFilter.config.id
        }
      }
      return headerText || fieldId
    }

    const getErrorFieldReasonMessage = function (
      reason: PlayErrorReason,
      errorField: PlayErrorField,
    ): string {
      let reasonMessage: string
      let fieldId: string
      if (!Array.isArray(errorField.fieldId)) {
        fieldId = errorField.fieldId.split('.').pop()
      } else {
        fieldId = errorField.fieldId.map(field => field.split('.').pop()).join(', ')
      }
      const fieldMessages = props.messages.fields[fieldId]
      if (!!fieldMessages && !!fieldMessages.errors) {
        reasonMessage = fieldMessages.errors[reason.id]
      }
      return reasonMessage || $epb.messages.errors[400].reasons[reason.id] || reason.message
    }

    watch(
      () => props.errorsFields,
      () => {
        showErrorContainer.value = !!props.errorsFields.length

        if (showErrorContainer.value) {
          emit('resetScroll')
        }
      },
    )

    return {
      baseClass,
      showErrorContainer,
      configurationSections,
      getErrorFieldHeaderText,
      getErrorFieldReasonMessage,
      ColorEnum,
    }
  },
})
